import React, { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useCreateMarketingRouter,
  useMarketingRouterRules,
  useUpdateMarketingRouter,
} from '~/hooks/apis/promo/useMarketingRouterRules';
import useCurrentTenant from '~/hooks/useCurrentTenant';

import type { MarketingRouterCreatePayload, MarketingRouterInternalQuery } from '~/services/PromoService';

import {
  type MarketingRouterRule,
  type MarketingRouterRuleSignificanceMetric,
  getMarketingRouterRuleCookieName,
} from '~/types/marketingRouterRule';

import { normalizeToISODate } from '~/utils/dateFormatter';

type RouteParams = { ruleId?: string };
type Variant = MarketingRouterRule['variants'][number];

const SIGNIFICANCE_METRICS: Array<{ value: MarketingRouterRuleSignificanceMetric; label: string }> = [
  { value: 'ttv', label: 'TTV' },
  { value: 'margin', label: 'Margin' },
  { value: 'conversion', label: 'Conversion' },
];

const emptyRule: MarketingRouterRule = {
  id: '',
  name: '',
  significance_metrics: ['ttv'],
  start_date: '',
  end_date: '',
  match: { utm_source: '', utm_medium: '', utm_campaign: '' },
  variants: [
    {
      variant_id: '',
      variant_name: '',
      weight: 100,
      is_control: true,
    },
  ],
};

export default function MarketingRouterRuleFormPage() {
  const navigate = useNavigate();
  const { ruleId } = useParams<RouteParams>();
  const { tenant } = useCurrentTenant();
  const query: MarketingRouterInternalQuery = {
    brand: tenant.brand as MarketingRouterInternalQuery['brand'],
    page: 0,
    pageSize: 100,
  };
  const rulesQuery = useMarketingRouterRules({ query });
  const rules = rulesQuery.data ?? [];
  const existingRule = useMemo(() => rules.find((rule) => rule.id === ruleId), [ruleId, rules]);
  const createMarketingRouterMutation = useCreateMarketingRouter();
  const updateMarketingRouterMutation = useUpdateMarketingRouter();
  const [rule, setRule] = useState<MarketingRouterRule>(() => existingRule ?? emptyRule);
  const [error, setError] = useState<string | null>(null);
  const isEditing = Boolean(ruleId);
  const isSaving = createMarketingRouterMutation.isPending || updateMarketingRouterMutation.isPending;

  useEffect(() => {
    if (existingRule) {
      setRule(existingRule);
    }
  }, [existingRule]);
  const variantsTotal = rule.variants.reduce((total, variants) => total + variants.weight, 0);
  const hasInvalidVariantTotal = variantsTotal !== 100;

  const updateMatch = (field: keyof MarketingRouterRule['match'], value: string) => {
    setRule((currentRule) => ({ ...currentRule, match: { ...currentRule.match, [field]: value } }));
  };

  const updateVariant = (index: number, field: keyof Variant, value: string) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) =>
        variantsIndex === index ? { ...variants, [field]: field === 'weight' ? Number(value) : value } : variants,
      ),
    }));
  };

  const addVariant = () => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: [
        ...currentRule.variants,
        {
          variant_id: '',
          variant_name: '',
          weight: 0,
          is_control: false,
        },
      ],
    }));
  };

  const removeVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.filter((_, variantsIndex) => variantsIndex !== index),
    }));
  };

  const setControlVariant = (index: number) => {
    setRule((currentRule) => ({
      ...currentRule,
      variants: currentRule.variants.map((variants, variantsIndex) => ({
        ...variants,
        is_control: variantsIndex === index,
      })),
    }));
  };

  const toggleSignificanceMetric = (metric: MarketingRouterRuleSignificanceMetric) => {
    setRule((currentRule) => ({
      ...currentRule,
      significance_metrics: currentRule.significance_metrics.includes(metric)
        ? currentRule.significance_metrics.filter((selectedMetric) => selectedMetric !== metric)
        : [...currentRule.significance_metrics, metric],
    }));
  };

  const moveSignificanceMetric = (index: number, direction: -1 | 1) => {
    setRule((currentRule) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= currentRule.significance_metrics.length) {
        return currentRule;
      }

      const metrics = [...currentRule.significance_metrics];
      [metrics[index], metrics[nextIndex]] = [metrics[nextIndex], metrics[index]];
      return { ...currentRule, significance_metrics: metrics };
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const ruleId = rule.id || globalThis.crypto.randomUUID();
    const variantsTotal = rule.variants.reduce((total, variants) => total + variants.weight, 0);
    const hasDuplicateVariants =
      new Set(rule.variants.map((variants) => variants.variant_name.trim())).size !== rule.variants.length;
    const controlCount = rule.variants.filter((variants) => variants.is_control).length;

    if (
      !rule.name.trim() ||
      !rule.start_date ||
      !rule.end_date ||
      !rule.match.utm_source.trim() ||
      !rule.match.utm_medium.trim() ||
      !rule.match.utm_campaign.trim()
    ) {
      setError('Complete the rule name, start date, end date, and UTM match fields.');
      return;
    }
    if (dayjs(rule.end_date).isBefore(dayjs(rule.start_date), 'day')) {
      setError('End date must be on or after the start date.');
      return;
    }
    if (rule.significance_metrics.length < 1 || rule.significance_metrics.length > 3) {
      setError('Select between one and three significance metrics.');
      return;
    }
    if (
      rule.variants.length === 0 ||
      rule.variants.some((variants) => !variants.variant_name.trim() || variants.weight < 0 || variants.weight > 100)
    ) {
      setError('Add at least one variant with a weight between 0 and 100.');
      return;
    }
    if (hasDuplicateVariants) {
      setError('Each variant name must be unique within a rule.');
      return;
    }
    if (controlCount !== 1) {
      setError('Select exactly one control variant. All other variants are test variants.');
      return;
    }
    if (variantsTotal !== 100) {
      setError(`Variant weights must total 100%. Current total: ${variantsTotal}%.`);
      return;
    }
    if (!isEditing && rules.some((currentRule) => currentRule.id === ruleId)) {
      setError('A rule with the generated ID already exists. Please try again.');
      return;
    }

    const preparedRule: MarketingRouterRule = {
      ...rule,
      id: ruleId,
      name: rule.name.trim(),
      start_date: normalizeToISODate(rule.start_date),
      end_date: normalizeToISODate(rule.end_date),
      match: {
        ...rule.match,
        utm_source: rule.match.utm_source.trim(),
        utm_medium: rule.match.utm_medium.trim(),
        utm_campaign: rule.match.utm_campaign.trim(),
      },
      variants: rule.variants.map((variants) => ({
        ...variants,
        variant_id: variants.variant_id || globalThis.crypto.randomUUID(),
        variant_name: variants.variant_name.trim(),
      })),
    };

    try {
      if (isEditing && existingRule) {
        const ruleJson = existingRule.ruleJson.map((candidate) => ({
          ...candidate,
          start_date:
            candidate.id === preparedRule.id ? preparedRule.start_date : normalizeToISODate(candidate.start_date),
          end_date: candidate.id === preparedRule.id ? preparedRule.end_date : normalizeToISODate(candidate.end_date),
          ...(candidate.id === preparedRule.id ? preparedRule : {}),
        }));
        await updateMarketingRouterMutation.mutateAsync({
          marketingRouterId: existingRule.marketingRouterId,
          brand: existingRule.brand as MarketingRouterCreatePayload['brand'],
          region: existingRule.region as MarketingRouterCreatePayload['region'],
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          ruleJson,
        });
      } else {
        await createMarketingRouterMutation.mutateAsync({
          brand: tenant.brand as MarketingRouterCreatePayload['brand'],
          startDate: preparedRule.start_date,
          endDate: preparedRule.end_date,
          ruleJson: [preparedRule],
        });
      }
      navigate('/marketing/marketing-router-rules');
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : 'Failed to save marketing router rule.');
    }
  };

  if (isEditing && rulesQuery.isLoading) {
    return (
      <Container maxWidth="xl">
        <Typography color="text.secondary">Loading marketing router rule…</Typography>
      </Container>
    );
  }

  if (isEditing && rulesQuery.isError) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">
          {rulesQuery.error instanceof Error ? rulesQuery.error.message : 'Failed to load marketing router rule.'}
        </Alert>
      </Container>
    );
  }

  if (isEditing && !existingRule) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Marketing Router Rule “{ruleId}” was not found.</Alert>
        <Button component={Link} to="/marketing/marketing-router-rules" variant="text" sx={{ mt: 2 }}>
          Back to rules
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Marketing / Edit Router Rule' : 'Marketing / Create Router Rule'}</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title={isEditing ? `Edit Marketing Router Rule: ${rule.id}` : 'Create Marketing Router Rule'} />
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={3}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Stack spacing={2}>
              <Typography variant="h6">Rule details</Typography>
              {isEditing ? (
                <TextField label="Rule ID" value={rule.id} disabled />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Rule ID will be generated automatically when the rule is saved.
                </Typography>
              )}
              <TextField
                label="Rule name"
                required
                fullWidth
                value={rule.name}
                onChange={(event) => setRule((current) => ({ ...current, name: event.target.value }))}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Start date"
                  required
                  type="date"
                  fullWidth
                  value={rule.start_date}
                  onChange={(event) => setRule((current) => ({ ...current, start_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End date"
                  required
                  type="date"
                  fullWidth
                  value={rule.end_date}
                  onChange={(event) => setRule((current) => ({ ...current, end_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Typography variant="h6">Match conditions</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="UTM source"
                  required
                  fullWidth
                  value={rule.match.utm_source}
                  onChange={(event) => updateMatch('utm_source', event.target.value)}
                />
                <TextField
                  label="UTM medium"
                  required
                  fullWidth
                  value={rule.match.utm_medium}
                  onChange={(event) => updateMatch('utm_medium', event.target.value)}
                />
                <TextField
                  label="UTM campaign"
                  required
                  fullWidth
                  value={rule.match.utm_campaign}
                  onChange={(event) => updateMatch('utm_campaign', event.target.value)}
                />
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Typography variant="h6">Metrics</Typography>
              <Typography color="text.secondary" variant="body2">
                Select the metrics used to measure statistical significance. The selected order is used in calculations.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {SIGNIFICANCE_METRICS.map((metric) => (
                  <FormControlLabel
                    key={metric.value}
                    control={
                      <Checkbox
                        checked={rule.significance_metrics.includes(metric.value)}
                        onChange={() => toggleSignificanceMetric(metric.value)}
                      />
                    }
                    label={metric.label}
                  />
                ))}
              </Stack>
              <Stack spacing={1}>
                {rule.significance_metrics.map((metric, index) => (
                  <Stack key={metric} direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ minWidth: 24 }}>{index + 1}.</Typography>
                    <Typography sx={{ minWidth: 120 }}>
                      {SIGNIFICANCE_METRICS.find((option) => option.value === metric)?.label}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label={`Move ${metric} up`}
                      onClick={() => moveSignificanceMetric(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Move ${metric} down`}
                      onClick={() => moveSignificanceMetric(index, 1)}
                      disabled={index === rule.significance_metrics.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Variant</Typography>
                <Button type="button" variant="text" onClick={addVariant}>
                  Add variant
                </Button>
              </Stack>
              {hasInvalidVariantTotal ? (
                <Alert severity="error">Variant weights must total 100%. Current total: {variantsTotal}%.</Alert>
              ) : null}
              {rule.variants.map((variants, index) => (
                <Stack
                  key={`${variants.variant_id}-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ sm: 'center' }}
                >
                  <TextField
                    label="Variant name"
                    required
                    fullWidth
                    value={variants.variant_name}
                    onChange={(event) => updateVariant(index, 'variant_name', event.target.value)}
                  />
                  <TextField
                    label="Weight (%)"
                    required
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    value={variants.weight}
                    onChange={(event) => updateVariant(index, 'weight', event.target.value)}
                    sx={{ minWidth: { sm: 160 } }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={variants.is_control}
                        onChange={() => setControlVariant(index)}
                        inputProps={{ 'aria-label': `Set variant ${index + 1} as control` }}
                      />
                    }
                    label="Control"
                    sx={{ minWidth: { sm: 110 } }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: 190 } }}>
                    Cookie:{' '}
                    {getMarketingRouterRuleCookieName(rule.id || 'rule_id', variants.variant_id || 'variant_id')}
                  </Typography>
                  <IconButton
                    aria-label={`Remove variant ${index + 1}`}
                    onClick={() => removeVariant(index)}
                    disabled={rule.variants.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button type="submit" variant="contained" color="primary" disabled={isSaving}>
                {isEditing ? 'Update rule' : 'Create rule'}
              </Button>
              <Button component={Link} to="/marketing/marketing-router-rules" variant="text">
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Container>
    </>
  );
}
